/**
 * Permessi Piattaforma (string-based)
 * Distinti dai permessi Hub/App/Feature configurati in hub-permissions.ts
 *
 * Regole:
 * - formato: area:azione (lowercase)
 * - admin ha implicitamente tutti i permessi (role === 'admin' || scopes includes '*')
 * - array compatto nel token Firebase custom claims
 */

export const PLATFORM_PERMISSIONS = {
  // Dataset / Export
  DATASET_READ: 'dataset:read',
  DATASET_INGEST: 'dataset:ingest',
  DATASET_DELETE: 'dataset:delete',
  DATASET_BRIDGE: 'dataset:bridge',
  EXPORT_READ: 'export:read',
  EXPORT_CREATE: 'export:create',

  // Dati sensibili modulo-specifici
  BUSINESS_READ_SENSITIVE: 'business:read_sensitive',

  // Dashboard
  BUSINESS_DASHBOARD_FULL_ACCESS: 'business:dashboard_full_access',

  // Fornitori
  SUPPLIER_READ: 'supplier:read',
  SUPPLIER_IMPORT: 'supplier:import',
  SUPPLIER_VIEW_LOG: 'supplier:view_log',
  SUPPLIER_NOTE_WRITE: 'supplier_note:write',
  SUPPLIER_CONTACT_WRITE: 'supplier_contact:write',
  SUPPLIER_CONTACT_READ_ARCHIVED: 'supplier_contact:read_archived',
} as const;

export type PlatformPermission = typeof PLATFORM_PERMISSIONS[keyof typeof PLATFORM_PERMISSIONS];

export const ALL_PLATFORM_PERMISSIONS: PlatformPermission[] = Object.values(PLATFORM_PERMISSIONS);

/**
 * Permessi modulo che in 1A.18.3 saranno spostati in permessi Hub/App.
 * Per ora rimangono nel codebase per backward compatibility,
 * ma vengono visualizzati separatamente nella UI permessi.
 */
export const MODULE_PERMISSION_KEYS: readonly string[] = [
  // Dataset / Export
  'dataset:read', 'dataset:ingest', 'dataset:delete', 'dataset:bridge',
  'export:read', 'export:create',
  // Fornitori
  'supplier:read', 'supplier:import', 'supplier:view_log',
  'supplier_note:write', 'supplier_contact:write', 'supplier_contact:read_archived',
  // Dashboard
  'business:dashboard_full_access',
  // Dati sensibili modulo-specifici
  'business:read_sensitive',
];

export const SENSITIVE_PLATFORM_PERMISSIONS: PlatformPermission[] = [
  'dataset:delete',
  'export:create',
  'business:read_sensitive',
];

export interface PlatformPermissionDefinition {
  key: PlatformPermission;
  label: string;
  description: string;
  category: string;
  sensitive: boolean;
}

export const PLATFORM_PERMISSION_DEFINITIONS: PlatformPermissionDefinition[] = [
  // Dataset / Export
  { key: 'dataset:read', label: 'Visualizza dataset', description: 'Accede ai dataset caricati.', category: 'Dataset ed export', sensitive: false },
  { key: 'dataset:ingest', label: 'Ingest dataset', description: 'Importa nuovi dataset nel sistema.', category: 'Dataset ed export', sensitive: false },
  { key: 'dataset:delete', label: 'Elimina dataset', description: 'Rimuove dataset dal sistema.', category: 'Dataset ed export', sensitive: true },
  { key: 'dataset:bridge', label: 'Bridge dataset', description: 'Sincronizza dataset verso Firestore.', category: 'Dataset ed export', sensitive: false },
  { key: 'export:read', label: 'Visualizza export', description: 'Accede agli export generati.', category: 'Dataset ed export', sensitive: false },
  { key: 'export:create', label: 'Crea export', description: 'Genera nuovi export/report.', category: 'Dataset ed export', sensitive: true },

  // Dashboard
  { key: 'business:dashboard_full_access', label: 'Dashboard - Controllo completo', description: 'Abilita l\'intero modulo Dashboard (tutti gli ambiti, tutti i CDC, tab Dettagli e Analisi).', category: 'Dashboard', sensitive: false },

  // Dati sensibili
  { key: 'business:read_sensitive', label: 'Leggi dati sensibili business', description: 'Accede a dati sensibili del modulo business.', category: 'Dati sensibili', sensitive: true },

  // Le chiavi legacy Fornitori restano in PLATFORM_PERMISSIONS e MODULE_PERMISSION_KEYS
  // per la compatibility backend, ma non vengono più esposte come capability UI.
];

/**
 * Verifica se un permesso è un permesso modulo (da spostare in Hub/App in 1A.18.3)
 */
export function isModulePermission(permission: string): boolean {
  return MODULE_PERMISSION_KEYS.includes(permission);
}

/**
 * [1A.18.3] Destinazione Hub/App target per i permessi modulo legacy.
 * Utile per UI e documentazione durante la transizione.
 */
export const MODULE_PERMISSION_TARGET_MAP: Record<string, { hub: string; app?: string; note?: string }> = {
  'supplier:read': { hub: 'business', app: 'fornitori' },
  'supplier:import': { hub: 'business', app: 'fornitori' },
  'supplier:view_log': { hub: 'business', app: 'fornitori' },
  'supplier_note:write': { hub: 'business', app: 'fornitori' },
  'supplier_contact:write': { hub: 'business', app: 'fornitori' },
  'supplier_contact:read_archived': { hub: 'business', app: 'fornitori' },
  'business:read_sensitive': { hub: 'business', app: 'dashboard', note: 'Dati sensibili' },
  'business:dashboard_full_access': { hub: 'business', app: 'dashboard', note: 'Controllo completo' },
};

export function getModulePermissionTarget(permission: string): { hub: string; app?: string; note?: string } | null {
  return MODULE_PERMISSION_TARGET_MAP[permission] || null;
}

/**
 * Verifica se un permesso è sensibile
 */
export function isSensitivePermission(permission: string): boolean {
  return SENSITIVE_PLATFORM_PERMISSIONS.includes(permission as PlatformPermission);
}

/**
 * Ottiene le definizioni raggruppate per categoria
 */
export function getPermissionDefinitionsByCategory(): Record<string, PlatformPermissionDefinition[]> {
  const groups: Record<string, PlatformPermissionDefinition[]> = {};
  for (const def of PLATFORM_PERMISSION_DEFINITIONS) {
    if (!groups[def.category]) groups[def.category] = [];
    groups[def.category].push(def);
  }
  return groups;
}
